import { useMemo } from 'react'
import { useAdminGroupSuggestions } from '../../hooks/useAdminGroupSuggestions'
import { useEventsByIds } from '../../hooks/useEventsByIds'
import { useUserProfiles } from '../../hooks/useUserProfiles'
import { approveGroupEventRequest, rejectGroupEventRequest } from '../../hooks/useGroupPendingRequests'
import { beaconUserFallback } from '../../lib/commonStrings'
import type { Event, Group, GroupEventRequest } from '../../types'

export interface InboxSuggestion {
  request: GroupEventRequest
  group: Group
  event: Event
  requesterName: string
}

/** Events suggested to groups the viewer admins, ready for Crew's inbox rows, with approve / decline. */
export function useInboxSuggestions(userId: string | null) {
  const { suggestions } = useAdminGroupSuggestions(userId)
  const eventIds = useMemo(() => [...new Set(suggestions.map(s => s.request.eventId))], [suggestions])
  const requesterIds = useMemo(() => [...new Set(suggestions.map(s => s.request.requestedBy))], [suggestions])
  const { events } = useEventsByIds(eventIds)
  const profiles = useUserProfiles(requesterIds)

  const rows = useMemo(() => {
    const byId = new Map(events.map(e => [e.id, e]))
    return suggestions.flatMap(({ request, group }): InboxSuggestion[] => {
      const event = byId.get(request.eventId)
      if (!event) return []
      const requesterName = profiles[request.requestedBy]?.displayName?.trim() || beaconUserFallback()
      return [{ request, group, event, requesterName }]
    })
  }, [suggestions, events, profiles])

  return {
    suggestions: rows,
    approve: (s: InboxSuggestion) => (userId ? approveGroupEventRequest(s.request, userId, false) : Promise.resolve()),
    decline: (s: InboxSuggestion) => (userId ? rejectGroupEventRequest(s.request, userId) : Promise.resolve()),
  }
}
